import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import CustomerLayout from "../../../layouts/CustomerLayout";
import paymentRequests from "../../../api/Customer/PaymentRequests"; // You'll need to create this
import {
  Search,
  Filter,
  Calendar,
  Download,
  Eye,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Package,
} from "lucide-react";

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [packageFilter, setPackageFilter] = useState("all");
  const [networkFilter, setNetworkFilter] = useState("all");

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load payment history
  const loadPaymentHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await paymentRequests.getPaymentHistory(
        pagination.currentPage,
        pagination.perPage,
        debouncedSearchTerm,
        "", // sortBy
        "desc", // sortOrder - default to newest first
        dateFrom,
        dateTo,
        statusFilter !== "all" ? statusFilter : "",
        packageFilter !== "all" ? packageFilter : "",
        networkFilter !== "all" ? networkFilter : ""
      );
      
      if (response.success && response.data?.data?.status_code === "200") {
        const paymentsData = response.data.data.data?.payments || [];
        const paginationData = response.data.data.data?.pagination || {};
        
        setPayments(Array.isArray(paymentsData) ? paymentsData : []);
        setPagination({
          currentPage: paginationData.current_page || pagination.currentPage,
          perPage: paginationData.per_page || pagination.perPage,
          total: paginationData.total || 0,
          lastPage: paginationData.last_page || 1,
          from: paginationData.from || 0,
          to: paginationData.to || 0,
        });
      } else {
        toast.error("Failed to load payment history");
        setPayments([]);
      }
    } catch (error) {
      console.error("Error loading payment history:", error);
      toast.error("Error loading payment history");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearchTerm,
    dateFrom,
    dateTo,
    statusFilter,
    packageFilter,
    networkFilter,
  ]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadPaymentHistory();
  }, [loadPaymentHistory]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  // Handle per page change
  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, currentPage: 1 }));
  };

  // Handle date filter change
  const handleDateFilterChange = (from, to) => {
    setDateFrom(from || "");
    setDateTo(to || "");
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("all");
    setPackageFilter("all");
    setNetworkFilter("all");
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        color: "bg-green-100 text-green-800 border-green-200",
        icon: <CheckCircle className="w-3 h-3" />,
        label: "Paid"
      },
      pending: {
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: <Clock className="w-3 h-3" />,
        label: "Pending"
      },
      failed: {
        color: "bg-red-100 text-red-800 border-red-200",
        icon: <XCircle className="w-3 h-3" />,
        label: "Failed"
      },
    };

    const config = statusConfig[status?.toLowerCase()] || {
      color: "bg-gray-100 text-gray-800 border-gray-200",
      icon: <Clock className="w-3 h-3" />,
      label: status || "Unknown"
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  // Get network badge
  const getNetworkBadge = (network) => {
    const networkConfig = {
      mtn: "bg-yellow-100 text-yellow-800 border-yellow-200",
      atg: "bg-blue-100 text-blue-800 border-blue-200",
      vodafone: "bg-red-100 text-red-800 border-red-200",
      airteltigo: "bg-purple-100 text-purple-800 border-purple-200",
    };

    const color = networkConfig[network?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${color}`}>
        {network || "Unknown"}
      </span>
    );
  };

  // Get package badge
  const getPackageBadge = (packageName) => {
    const packageConfig = {
      bronze: "bg-amber-100 text-amber-800 border-amber-200",
      silver: "bg-gray-100 text-gray-800 border-gray-200",
      gold: "bg-yellow-100 text-yellow-800 border-yellow-200",
      platinum: "bg-blue-100 text-blue-800 border-blue-200",
    };

    const color = packageConfig[packageName?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${color}`}>
        {packageName || "Unknown"}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate stats
  const calculateStats = () => {
    const totalPayments = payments.length;
    const totalAmount = payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    const successfulPayments = payments.filter(p => p.status?.toLowerCase() === "paid").length;
    const failedPayments = payments.filter(p => p.status?.toLowerCase() === "failed").length;
    const pendingPayments = payments.filter(p => p.status?.toLowerCase() === "pending").length;
    
    return {
      totalPayments,
      totalAmount,
      successfulPayments,
      failedPayments,
      pendingPayments,
    };
  };

  const stats = calculateStats();

  // Available filters
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
    { value: "failed", label: "Failed" },
  ];

  const packageOptions = [
    { value: "all", label: "All Packages" },
    { value: "bronze", label: "Bronze" },
    { value: "silver", label: "Silver" },
    { value: "gold", label: "Gold" },
    { value: "platinum", label: "Platinum" },
  ];

  const networkOptions = [
    { value: "all", label: "All Networks" },
    { value: "mtn", label: "MTN" },
    { value: "atg", label: "ATG" },
    { value: "vodafone", label: "Vodafone" },
    { value: "airteltigo", label: "AirtelTigo" },
  ];

  // Export to CSV
  const handleExportCSV = () => {
    if (payments.length === 0) {
      toast.info("No data to export");
      return;
    }

    const headers = ["ID", "Date", "Reference", "Phone", "Network", "Package", "Plan", "Units", "Amount", "Status"];
    const csvRows = [
      headers.join(","),
      ...payments.map(payment => [
        payment.id,
        formatDate(payment.created_at),
        payment.reference_id || "N/A",
        payment.phone_number,
        payment.network,
        payment.package_name,
        payment.plan_name,
        payment.units,
        payment.amount,
        payment.status,
      ].join(","))
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `payment-history-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Payment history exported successfully");
  };

  return (
    <CustomerLayout>
      <div className="py-4 pt-16 min-h-screen bg-gray-50 sm:py-6 sm:pt-20">
        <div className="px-3 mx-auto max-w-8xl sm:px-4 md:px-6 lg:px-8">
          {/* Header */}
          <div className="p-4 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-between sm:items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                  Payment History
                </h1>
                <p className="mt-1 text-sm text-gray-600 sm:text-base">
                  Track all your subscription payments and transactions
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleExportCSV}
                  disabled={payments.length === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                
                <button
                  onClick={loadPaymentHistory}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Total Payments</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.totalPayments}</p>
                  </div>
                  <CreditCard className="w-8 h-8 text-blue-500" />
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Total Amount</p>
                    <p className="text-2xl font-bold text-gray-800">{formatCurrency(stats.totalAmount)}</p>
                  </div>
                  <p className="text-[2rem] font-bold text-green-600">₵</p>
                  {/* <DollarSign className="w-8 h-8 text-green-500" /> */}
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-600">Successful</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.successfulPayments}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pending/Failed</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.pendingPayments + stats.failedPayments}</p>
                  </div>
                  <Clock className="w-8 h-8 text-gray-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="p-4 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl sm:p-6">
            <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search by reference, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Date Filters */}
              <div className="flex gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => handleDateFilterChange(e.target.value, dateTo)}
                  className="px-3 py-2 w-full text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="From"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => handleDateFilterChange(dateFrom, e.target.value)}
                  className="px-3 py-2 w-full text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="To"
                />
              </div>
              
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              {/* Package Filter */}
              <select
                value={packageFilter}
                onChange={(e) => {
                  setPackageFilter(e.target.value);
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {packageOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Additional Filters */}
            <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
              {/* Network Filter */}
              <select
                value={networkFilter}
                onChange={(e) => {
                  setNetworkFilter(e.target.value);
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {networkOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              {/* Clear Filters */}
              <div className="flex justify-end">
                <button
                  onClick={handleClearAllFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>

          {/* Payment History Table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
                  <span className="text-gray-600">Loading payment history...</span>
                </div>
              </div>
            ) : payments.length === 0 ? (
              <div className="p-8 text-center">
                <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
                  <CreditCard className="text-2xl text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No Payments Found
                </h3>
                <p className="text-gray-500 mb-4">
                  {debouncedSearchTerm || dateFrom || dateTo || statusFilter !== "all" || packageFilter !== "all" || networkFilter !== "all"
                    ? "No payments match your filters. Try adjusting your search criteria."
                    : "You haven't made any payments yet."}
                </p>
                {(debouncedSearchTerm || dateFrom || dateTo || statusFilter !== "all" || packageFilter !== "all" || networkFilter !== "all") && (
                  <button
                    onClick={handleClearAllFilters}
                    className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Transaction
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Package & Plan
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th> */}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-gray-900">
                                Ref: {payment.reference_id || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {payment.phone_number}
                              </div>
                              <div className="flex items-center gap-2">
                                {getNetworkBadge(payment.network)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-gray-400" />
                                {getPackageBadge(payment.package_name)}
                              </div>
                              <div className="text-sm text-gray-600">
                                {payment.plan_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {payment.units} units
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-lg font-semibold text-gray-900">
                              {formatCurrency(payment.amount)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(payment.status)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {formatDate(payment.created_at)}
                            </div>
                            {payment.expires_at && (
                              <div className="text-xs text-gray-500">
                                Expires: {new Date(payment.expires_at).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          {/* <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                // View payment details - you can implement a modal or expandable row
                                //console.log("View payment:", payment);
                                toast.info("View payment details feature coming soon");
                              }}
                              className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              Details
                            </button>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.total > pagination.perPage && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="text-sm text-gray-600">
                        Showing {pagination.from} to {pagination.to} of {pagination.total} payments
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        {/* Per page selector */}
                        <select
                          value={pagination.perPage}
                          onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={5}>5 per page</option>
                          <option value={10}>10 per page</option>
                          <option value={15}>15 per page</option>
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                        </select>
                        
                        {/* Page navigation */}
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                            className={`p-2 rounded-lg ${pagination.currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(pagination.lastPage, 5) }, (_, i) => {
                              let pageNum;
                              if (pagination.lastPage <= 5) {
                                pageNum = i + 1;
                              } else if (pagination.currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (pagination.currentPage >= pagination.lastPage - 2) {
                                pageNum = pagination.lastPage - 4 + i;
                              } else {
                                pageNum = pagination.currentPage - 2 + i;
                              }
                              
                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  className={`px-3 py-1 text-sm rounded-lg ${pagination.currentPage === pageNum ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}`}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>
                          
                          <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.lastPage}
                            className={`p-2 rounded-lg ${pagination.currentPage === pagination.lastPage ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"}`}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default PaymentHistory;